<template>
  <div class="px-5 lg:px-0 max-w-360 mx-auto pb-10 lg:pb-20">
    <form @submit.prevent="onUpdateSettings">
      <fieldset :disabled="loading || submitting">
        <h2
          class="text-blue-brand font-primary font-extrabold text-28 md:text-32 -tracking-1/2 leading-1 my-5"
        >
          Settings
        </h2>
        <h3
          class="text-blue-brand font-primary font-extrabold text-24 md:text-26 -tracking-1/2 leading-1 my-5 pb-5 border-b border-black-0.1"
        >
          Your Account
        </h3>
        <label for="settingsName" class="label block mb-2">Name</label>
        <input
          type="text"
          name="settingsName"
          class="input block w-full mb-5"
          placeholder="Example name"
          v-model="name"
        />
        <error-message :errors="getErrors('name')"></error-message>
        <label for="settingsEmail" class="label block mb-2">Account Email</label>
        <input
          type="email"
          name="settingsEmail"
          class="input block w-full mb-5"
          placeholder="name@email.com"
          v-model="email"
        />
        <error-message :errors="getErrors('email')"></error-message>
        <h3
          class="text-blue-brand font-primary font-extrabold text-24 md:text-26 -tracking-1/2 leading-1 my-5 pb-5 border-b border-black-0.1"
        >
          Change Password
        </h3>
        <label for="settingsOldPassword" class="label block mb-2">Old Password</label>
        <input
          type="password"
          name="settingsOldPassword"
          class="input block w-full mb-5"
          placeholder="Old password..."
          v-model="oldPassword"
        />
        <error-message :errors="getErrors('old_password')"></error-message>
        <label for="settingsNewPassword" class="label block mb-2">New Password</label>
        <input
          type="password"
          name="settingsNewPassword"
          class="input block w-full mb-5"
          placeholder="New password..."
          v-model="newPassword"
        />
        <error-message :errors="getErrors('password')"></error-message>
        <label for="settingsConfirmNewPassword" class="label block mb-2"
          >Re-type New Password</label
        >
        <input
          type="password"
          name="settingsConfirmNewPassword"
          class="input block w-full mb-5"
          placeholder="Password again..."
          v-model="confirmNewPassword"
        />
        <error-message :errors="getErrors('confirm_password')"></error-message>

        <h3
          class="text-blue-brand font-primary font-extrabold text-24 md:text-26 -tracking-1/2 leading-1 my-5 pb-5 border-b border-black-0.1"
        >
          Contact Details
        </h3>
        <div class="relative">
          <span
            v-if="hasEmailAndPhone"
            class="hidden md:block absolute top-0 -right-20 pl-1.5 label"
            >Preferred</span
          >
          <label for="settingsContactNumber" class="label block mb-2">Contact Number</label>
          <div class="relative pb-7.5 md:pb-0 mb-5 md:mb-0">
            <div class="md:mb-5">
              <phone-mask-input
                ref="phoneNumber"
                v-model="phone"
                autoDetectCountry
                inputClass="input block w-full"
                defaultCountry="UK"
                @onValidate="onUpdatePhoneNumber"
              ></phone-mask-input>
            </div>
            <error-message :errors="getErrors('phone')"></error-message>

            <span v-if="hasEmailAndPhone" class="md:hidden absolute bottom-0 left-0 label"
              >Preferred</span
            >
            <div
              v-if="hasEmailAndPhone"
              class="absolute bottom-0 right-0 md:h-full md:flex md:flex-col md:justify-center md:-right-10"
            >
              <div class="relative inline-block">
                <input
                  type="radio"
                  name="preferredContact"
                  value="phone"
                  v-model="preferredContact"
                  class="h-5 w-5 rounded cursor-pointer align-middle"
                />
                <span class="radio-dot"></span>
              </div>
            </div>
          </div>
        </div>

        <label for="settingsContactEmail" class="label block mb-2">Contact Email</label>
        <div class="relative pb-7.5 md:pb-0 mb-5 md:mb-0">
          <input
            type="email"
            name="settingsContactEmail"
            class="input block w-full md:mb-5"
            placeholder="name@email.com"
            v-model="contactEmail"
          />
          <error-message :errors="getErrors('contact_email')"></error-message>

          <span v-if="hasEmailAndPhone" class="absolute md:hidden bottom-0 left-0 label"
            >Preferred</span
          >

          <div
            v-if="hasEmailAndPhone"
            class="absolute bottom-0 right-0 md:h-full md:flex md:flex-col md:justify-center md:-right-10"
          >
            <div class="relative inline-block">
              <input
                type="radio"
                name="preferredContact"
                value="email"
                v-model="preferredContact"
                class="h-5 w-5 rounded cursor-pointer align-middle"
              />
              <span class="radio-dot"></span>
            </div>
          </div>
        </div>

        <label for="settingsDateOfBirth" class="label block mb-2">Date of Birth</label>
        <input
          type="date"
          name="settingsDateOfBirth"
          class="input block w-full mb-5"
          v-model="dateOfBirth"
        />
        <error-message :errors="getErrors('date_of_birth')"></error-message>
        <label for="settingsAddressLine1" class="label block mb-2">Address Line 1</label>
        <input
          type="text"
          name="settingsAddressLine1"
          class="input block w-full mb-5"
          placeholder="Address Line 1"
          v-model="addressLine1"
        />
        <error-message :errors="getErrors('address_line_1')"></error-message>
        <label for="settingsAddressLine2" class="label block mb-2">Address Line 2</label>
        <input
          type="text"
          name="settingsAddressLine2"
          class="input block w-full mb-5"
          placeholder="Address Line 2"
          v-model="addressLine2"
        />
        <error-message :errors="getErrors('address_line_2')"></error-message>
        <label for="settingsCity" class="label block mb-2">City</label>
        <input
          type="text"
          name="settingsCity"
          class="input block w-full mb-5"
          placeholder="City"
          v-model="city"
        />
        <error-message :errors="getErrors('city')"></error-message>
        <label for="settingsCounty" class="label block mb-2">County</label>
        <input
          type="text"
          name="settingsCounty"
          class="input block w-full mb-5"
          placeholder="County"
          v-model="county"
        />
        <error-message :errors="getErrors('county')"></error-message>
        <label for="settingsPostcode" class="label block mb-2">Postcode / Zip Code</label>
        <input
          type="text"
          name="settingsPostcode"
          class="input block w-full mb-5"
          placeholder="AB12 3CD"
          v-model="postcode"
        />
        <error-message :errors="getErrors('postcode')"></error-message>
        <label for="settingsCountry" class="label block mb-2">Country</label>
        <input
          type="text"
          name="settingsCountry"
          class="input block w-full mb-5"
          placeholder="Country"
          v-model="country"
        />
        <error-message :errors="getErrors('country')"></error-message>

        <div v-if="errorMessage" class="text-red-400 my-4">
          {{ errorMessage }}
        </div>

        <button
          type="submit"
          class="button min-w-0 w-full bg-yellow-brand text-blue-dark transition-colors hover:bg-yellow-light"
        >
          Update settings
        </button>
      </fieldset>
    </form>
    <form :action="logOut" method="POST">
      <input type="hidden" name="_token" :value="csrf" />
      <button type="submit" class="button min-w-0 w-full">Sign out</button>
    </form>
    <generic-tooltip v-if="showSuccessTooltip" @close="showSuccessTooltip = false">
      <h2 class="text-lg font-bold mb-5 text-blue-brand">Success</h2>
      <p class="mb-5">Your settings have been updated</p>
      <button type="button" class="button" @click.prevent="showSuccessTooltip = false">
        Close
      </button>
    </generic-tooltip>
  </div>
</template>

<script>
import ErrorMessage from './ErrorMessage.vue';
import HandlesErrors from './Mixins/HandlesErrors';
import PhoneMaskInput from  "vue-phone-mask-input";
import Route from '../src/Route.js';
import GenericTooltip from './GenericTooltip.vue';

export default {
  components: {
    ErrorMessage,
    PhoneMaskInput,
    GenericTooltip,
  },

  mixins: [HandlesErrors],

  data() {
    return {
      name: '',
      email: '',
      phone: '',
      oldPassword: '',
      newPassword: '',
      confirmNewPassword: '',
      contactEmail: '',
      preferredContact: 'phone',
      dateOfBirth: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postcode: '',
      country: '',
      loading: false,
      submitting: false,
      csrf: document.querySelector('meta[name=csrf-token]').content,
      showSuccessTooltip: false,
    };
  },

  computed: {
    logOut() {
      return Route('logout');
    },

    hasEmailAndPhone() {
      return !!this.contactEmail && !!this.phone;
    },
  },

  created() {
    this.getUserSettings();
  },

  methods: {
    getUserSettings() {
      this.loading = true;

      axios
        .get(Route('json.account.settings.edit'))
        .then((response) => {
          if (response.status === 200) {
            this.setUserSettings(response.data);
          } else {
            throw 'There was a problem.';
          }

          // This is needed for some reason to make the
          // phone input notice the change in value
          this.$nextTick(function () {
            this.$refs.phoneNumber.onInput(this.phone)
          });
        })
        .catch((error) => {
          console.log(error);
          this.error = 'Sorry there was a problem loading settings.';
        })
        .then(() => {
          this.loading = false;
        });
    },

    onUpdatePhoneNumber(payload) {
      if (payload.isValidByLibPhoneNumberJs) {
        this.phone = payload.number;
      }
    },

    onUpdateSettings() {
      this.submitting = true;
      this.resetErrors();

      const data = {
        name: this.name,
        email: this.email,
        old_password: this.oldPassword,
        password: this.newPassword,
        password_confirmation: this.confirmNewPassword,
        contact_email: this.contactEmail,
        phone: this.phone,
        date_of_birth: this.dateOfBirth,
        address_line_1: this.addressLine1,
        address_line_2: this.addressLine2,
        city: this.city,
        county: this.county,
        country: this.country,
        postcode: this.postcode,
        preferred_contact_method: this.preferredContact,
      };

      axios
        .post(Route('json.account.settings.update'), data)
        .then((response) => {
          if (response.status !== 200) {
            throw 'Network error.';
          }
          this.setUserSettings(response.data);
          this.showSuccessTooltip = true;
        })
        .catch(this.handleXHRErrors)
        .then(() => {
          this.submitting = false;
        });
    },

    setUserSettings(data) {
      this.name = data.user.name;
      this.email = data.user.email;
      this.contactEmail = data.user.contact_email;
      this.phone = data.user.phone;
      this.dateOfBirth = data.user.date_of_birth.slice(0, 10);
      this.addressLine1 = data.user.address_line_1;
      this.addressLine2 = data.user.address_line_2;
      this.city = data.user.city;
      this.county = data.user.county;
      this.country = data.user.country;
      this.postcode = data.user.postcode;
      this.preferredContact = data.user.preferred_contact_method
        ? data.user.preferred_contact_method
        : 'phone';
    },
  },
};
</script>
